<template>
    <b-form class="create-update-user-form" @submit.stop.prevent="onSubmit">
        <div v-if="isUpdateMode" id="tenants" class="group-wrapper">
            <label>Email</label>
            <div class="tenant-select-wrapper">
                <multiselect
                    v-model="user"
                    :options="users"
                    :multiple="false"
                    :clear-on-select="false"
                    :preserve-search="true"
                    placeholder=""
                    :preselect-first="true"
                    label="email"
                    track-by="_id"
                    :show-labels="false"
                    :loading="loadingUsers"
                    @close="checkForError('email')"
                    @select="setFormDataByUser"
                />
                <p>{{ validationErrors.email }}</p>
            </div>
        </div>

        <div v-if="!isUpdateMode" id="tenants" class="group-wrapper">
            <label>Email</label>
            <div class="email-input-wrapper">
                <b-form-input
                    id="email"
                    v-model="form.email"
                    required
                    type="email"
                    @blur="checkForError('email')"
                ></b-form-input>
                <p class="error error-message">{{ validationErrors.email }}</p>
            </div>
        </div>

        <div id="first-name" class="group-wrapper">
            <label>First Name</label>
            <div class="user-name-input-wrapper">
                <b-form-input
                    id="first-name"
                    v-model="form.firstName"
                    required
                    @blur="checkForError('firstName')"
                >
                </b-form-input>
                <p class="error error-message">{{ validationErrors.firstName }}</p>
            </div>
        </div>

        <div id="last-name" class="group-wrapper">
            <label>Last Name</label>
            <div class="user-name-input-wrapper">
                <b-form-input
                    id="last-name"
                    v-model="form.lastName"
                    required
                    @blur="checkForError('lastName')"
                ></b-form-input>
                <p class="error error-message">{{ validationErrors.lastName }}</p>
            </div>
        </div>

        <div v-if="!isUpdateMode" id="user-name" class="group-wrapper">
            <label>User Name</label>
            <div class="user-name-input-wrapper">
                <b-form-input
                    id="userName"
                    v-model="form.userName"
                    required
                    @focus="shouldHideError('userName')"
                    @blur="checkForError('userName')"
                ></b-form-input>
                <p>{{ validationErrors.userName }}</p>
            </div>
        </div>

        <div id="tenants" class="group-wrapper">
            <label>Tenants</label>
            <div class="tenant-select-wrapper">
                <multiselect
                    v-model="form.tenants"
                    :options="tenants"
                    :multiple="true"
                    :close-on-select="false"
                    :clear-on-select="false"
                    :preserve-search="true"
                    placeholder=""
                    :preselect-first="true"
                    label="name"
                    track-by="_id"
                    :show-labels="false"
                    @open="shouldHideError('tenants')"
                    @close="checkForError('tenants')"
                >
                </multiselect>
                <p>{{ validationErrors.tenants }}</p>
            </div>
        </div>

        <div id="tenants" class="group-wrapper userRoles">
            <label class="custom-label"> Professional Role </label>
            <div class="user-name-input-wrapper">
                <b-dropdown v-model="form.userRole" @hide="shouldHideError('userRole')">
                    <template v-slot:button-content>
                        <span>{{
                            form.userRole === 'clinic_manager'
                                ? $translate(`${form.userRole}`)
                                : form.userRole || 'Select Role'
                        }}</span>
                    </template>
                    <b-dropdown-item
                        v-for="role in userRoles"
                        :key="role.name"
                        @click="form.userRole = role.name"
                    >
                        <span>
                            {{
                                role.name === 'clinic_manager'
                                    ? $translate(`${role.name}`)
                                    : role.name
                            }}
                        </span>
                    </b-dropdown-item>
                </b-dropdown>
                <p>{{ validationErrors.userRole }}</p>
            </div>
        </div>

        <div id="image-url" class="group-wrapper">
            <label>Image URL</label>
            <div class="user-name-input-wrapper">
                <b-form-input
                    id="image-url"
                    v-model="form.imageUrl"
                    placeholder="Optional"
                ></b-form-input>
            </div>
        </div>

        <div v-if="!isUpdateMode" id="role" class="group-wrapper">
            <label>Role</label>
            <div class="buttons-wrapper">
                <b-form-radio v-model="form.role" value="agent">Agent</b-form-radio>
                <b-form-radio v-model="form.role" value="employee">Employee</b-form-radio>
            </div>
        </div>
        <div class="group-wrapper">
            <label>Products</label>
            <div class="user-name-input-wrapper">
                <b-form-group class="product-options">
                    <b-form-checkbox-group
                        v-model="form.products"
                        :options="form.productOptions"
                        @change="checkForError('products')"
                    />
                </b-form-group>
                <p class="error error-message">{{ validationErrors.products }}</p>
            </div>
        </div>
        <div v-if="!isUpdateMode" class="group-wrapper">
            <label>Temporary Password</label>
            <b-form-checkbox v-model="form.isPasswordTemp" :value="true" :unchecked-value="false" />
        </div>

        <div id="submit" class="group-wrapper">
            <b-button type="submit" variant="primary">Submit</b-button>
        </div>
    </b-form>
</template>

<script>
import BackendProvider from '../../../../business/services/BackendProvider'
import UIUtils from '../../../../business/utils/UIUtils'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.min.css'
import { allowedCaseOptions } from 'eslint-plugin-vue/lib/utils/casing'

export default {
    name: 'CreateUpdateUserForm',
    components: { Multiselect },
    props: {
        isUpdateMode: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            form: {
                userName: '',
                firstName: '',
                lastName: '',
                imageUrl: '',
                email: '',
                role: '',
                tenants: [],
                userRole: '',
                products: [],
                productOptions: [
                    { text: 'EQ', value: 'EQ' },
                    { text: 'KPI', value: 'KPI' },
                    { text: 'OQ', value: 'OQ' },
                    { text: 'DOCTOR', value: 'DOCTOR' }
                ],
                isPasswordTemp: false
            },
            user: null,
            users: [],
            tenants: [],
            userRoles: [],
            loadingUsers: false,
            validationErrors: {
                userName: '',
                firstName: '',
                lastName: '',
                email: '',
                tenants: '',
                products: ''
            },
            validationMessages: {
                DUPLICATE_USER: 'User exists with same username',
                DUPLICATE_EMAIL: 'User exists with same email',
                INVALID_EMAIL: 'Email must be in valid email format',
                MUST_SELECT: 'Please select a value'
            }
        }
    },
    computed: {
        tenantIdObjectsArray() {
            return this.form.tenants.map(t => {
                return {
                    _id: t._id
                }
            })
        },
        isFormInValid() {
            return Object.values(this.validationErrors).some(error => !!error)
        },
        allowedProductsFromTenants() {
            if (this.form.tenants.length) {
                return [...new Set(this.form.tenants.flatMap(x => x.products || [])).values()]
            }
            return []
        }
    },
    mounted() {
        this.loadUsers()
        this.loadTenants()
        this.loadUserRoles()
    },
    methods: {
        async loadUsers() {
            this.loadingUsers = true
            this.users = await BackendProvider.getAllUsers()
            this.loadingUsers = false
        },
        checkForError(id) {
            switch (id) {
                case 'email':
                    this.validationErrors.email =
                        !this.isUpdateMode && this.form.email === ''
                            ? 'Email is a required field'
                            : this.isUpdateMode && this.user == null
                            ? 'Email is a required field'
                            : ''
                    return
                case 'firstName':
                    this.validationErrors.firstName = !this.form.firstName
                        ? 'First Name is a required field'
                        : ''
                    return
                case 'lastName':
                    this.validationErrors.lastName = !this.form.lastName
                        ? 'Last Name is a required field'
                        : ''
                    return
                case 'tenants':
                    this.validationErrors.tenants =
                        this.isUpdateMode && this.form.tenants.length == 0
                            ? 'Tenant is required field'
                            : ''
                    return
                case 'products': {
                    this.validationErrors.products = ''
                    if (this.form.products.length === 0) {
                        this.validationErrors.products = 'Products is a required field'
                        return
                    }
                    const notAllowedProduct = this.form.products.filter(
                        product => !this.allowedProductsFromTenants.includes(product)
                    )
                    if (notAllowedProduct.length > 0) {
                        this.validationErrors.products = `${notAllowedProduct.join()} access missing from tenants`
                        return
                    }
                    return
                }
                case 'userName':
                    this.validationErrors.userName =
                        !this.isUpdateMode && !this.form.userName
                            ? 'UserName is required field'
                            : ''
            }
        },
        async onSubmit() {
            this.validateForm()
            if (this.isFormInValid) {
                return
            }
            UIUtils.showLoadingMask()

            try {
                if (!this.form.imageUrl) this.form.imageUrl = 'empty'

                if (this.isUpdateMode) {
                    const updatedUser = await this.updateUser()
                    this.$bvModal.hide('update_user')
                    UIUtils.toast(`user updated: ${updatedUser._id}`, 'message', 10 * 1000)
                } else {
                    const createdUser = await this.createUser()
                    this.$bvModal.hide('create_user')
                    UIUtils.toast(
                        `user name: ${this.form.userName} password: ${createdUser.password}`,
                        'message',
                        30 * 1000
                    )
                }
            } catch (e) {
                this.handleValidationError(e.message)
            }
            UIUtils.hideLoadingMask()
        },
        async createUser() {
            const {
                userName,
                firstName,
                lastName,
                email,
                imageUrl,
                role,
                isPasswordTemp,
                userRole,
                products
            } = this.form

            return await BackendProvider.createUser({
                userName,
                firstName,
                lastName,
                email,
                tenants: this.tenantIdObjectsArray,
                imageUrl,
                role: role || null,
                products,
                userRoles: userRole === '' ? [] : [userRole],
                isPasswordTemp
            })
        },
        async updateUser() {
            const { firstName, lastName, tenants, userRole, imageUrl, products } = this.form
            const userDetails = {}
            if (firstName !== '') {
                userDetails['firstName'] = firstName
            }
            if (lastName !== '') {
                userDetails['lastName'] = lastName
            }
            if (tenants.length > 0) {
                userDetails['tenants'] = tenants
            }
            if (products.length > 0) {
                userDetails['products'] = products
            }
            if (userRole) {
                userDetails['userRoles'] = userRole === '' ? [] : [userRole]
            }
            if (imageUrl !== '') {
                userDetails['imageUrl'] = imageUrl
            }
            return await BackendProvider.updateUser(this.user._id, userDetails)
        },
        validateForm() {
            Object.keys(this.validationErrors).forEach(key => this.checkForError(key))
        },
        validateOnlyInvalidField() {
            Object.keys(this.validationErrors)
                .filter(key => !!this.validationErrors[key])
                .forEach(key => this.checkForError(key))
        },
        handleValidationError(message) {
            switch (true) {
                case message === this.validationMessages.DUPLICATE_USER:
                    this.validationErrors.userName = message
                    break
                case message === this.validationMessages.DUPLICATE_EMAIL ||
                    message === this.validationMessages.INVALID_EMAIL:
                    this.validationErrors.email = message
                    break
                default:
                    UIUtils.toast(message, 'danger', 10 * 1000)
            }
        },
        setFormDataByUser(user) {
            if (!user) return
            const {
                firstName,
                lastName,
                tenants,
                userRoles,
                isPasswordTemp,
                imageUrl,
                role,
                products = []
            } = user
            if (firstName) {
                this.form.firstName = firstName
            }
            if (lastName) {
                this.form.lastName = lastName
            }
            if (tenants) {
                const selectedTenants = this.tenants.filter(
                    x => !!tenants.find(y => y._id === x._id)
                )
                this.form.tenants = [...selectedTenants]
                this.form.products = [
                    ...new Set(selectedTenants.flatMap(x => x.products || [])).values()
                ]
                // this.tenants.forEach(tenant => {
                //     tenants.forEach(_tenant =>
                //         _tenant._id === tenant._id ? this.form.tenants.push(tenant) : null
                //     )
                // })
            }
            if (products && products.length > 0) {
                this.form.products = products
            }
            if (userRoles) {
                this.form.userRole = userRoles[0]
            }
            if (isPasswordTemp) {
                this.form.isPasswordTemp = isPasswordTemp
            }
            if (imageUrl) {
                this.form.imageUrl = imageUrl || ''
            }
            if (role) {
                this.form.role = role || ''
            }
            this.validateOnlyInvalidField()
        },
        shouldHideError(id) {
            if (this.validationErrors[id]) this.validationErrors[id] = ''
        },
        async loadTenants() {
            this.tenants = await BackendProvider.getAllTenants()
        },
        async loadUserRoles() {
            this.userRoles = await BackendProvider.getAllUserRoles()
        }
    }
}
</script>

<style lang="scss" scoped>
.create-update-user-form {
    position: relative;
    width: 30rem;

    .group-wrapper {
        position: relative;
        display: grid;
        grid-template-columns: 40% 60%;
        align-items: center;
        padding: 0.5rem;
        min-height: 4.5rem;

        label {
            padding-top: 3%;
        }

        p {
            color: $red;
            font-size: 0.9rem;
            margin: 0;
            padding-top: 0.1rem;
        }

        .user-name-input-wrapper,
        .email-input-wrapper {
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            padding-top: 0.5rem;
        }

        .buttons-wrapper {
            display: flex;
            justify-content: space-around;
        }
    }

    #submit.group-wrapper {
        display: flex;
        justify-content: center;
    }
}
</style>

<!--override vue multiselect component styles-->
<style lang="scss">
#tenants.group-wrapper {
    .multiselect {
        z-index: 3;
        font-size: 0.9rem;
        position: relative;

        .error {
            color: $red;
            font-size: 0.9rem;
            margin: 0;
        }
        .error-message {
            margin-left: 100%;
            width: 100%;
        }
        .multiselect__tags {
            background-color: $dark-2;
            border-radius: 0.3rem;
            border-color: $light;

            .multiselect__tags-wrap {
                .multiselect__tag {
                    background-color: $blue;
                    .multiselect__tag-icon {
                        background-color: transparent;
                    }
                }
            }
            .multiselect__spinner {
                background: transparent;
            }
            .multiselect__placeholder {
                font-size: 0.9rem;
            }
            .multiselect__single {
                background: transparent;
                color: $white;
            }
            .multiselect__input {
                background-color: $dark-2;
                color: $light;
                font-size: 0.9rem;
            }
        }

        .multiselect__content-wrapper {
            position: absolute;
            max-height: 10rem;
            background-color: $dark-2;
            border-color: $light;
            border-radius: 0.3rem;

            .multiselect__content {
                background-color: $dark-2;
                color: $light-2;
                max-height: 10rem;

                .multiselect__element {
                    .multiselect__option--highlight {
                        background-color: $blue;
                    }
                    .multiselect__option--selected {
                        background-color: $blue;
                    }
                }
            }
        }
    }
    &.userRoles {
        z-index: 2;
    }

    .multiselect--active {
        position: absolute;
        top: 0.8rem;
        left: 12.1rem;
        width: 17.43rem;
        height: 100%;
    }
}
</style>
